import { format } from 'date-fns';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { InputSwitch } from 'primereact/inputswitch';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGatesNow } from '../GatesNowDataContext';
import {
    COLORS,
    STALE_MINUTES,
    STATUS_CLOSED_REGULAR,
    STATUS_OPEN_OUTSIDE,
    classifyUnitDiscrepancy,
    formatRelativeSensorLabel,
    getMarkerBackground,
    getSensorAgeMinutes,
    groupRowsByUnit,
    hasLiveDiscrepancy,
    isSensorStale,
} from '../utils/gateStatus';

const AIR_SIDE_CENTER = [-80.24615, 40.495341897380505];
const AIR_SIDE_ZOOM = 16.25;

const formatUnitAgeHtml = (timestamp) => {
    const { label, stale } = formatRelativeSensorLabel(timestamp);
    return `<span class="cardPopup-unit-age${stale ? ' is-stale' : ''}"><i class="fa-regular fa-clock" aria-hidden="true"></i>${label}</span>`;
};

const unitPopupStatusClass = (stateDescription, stale) => {
    const classes = ['cardPopup-unit'];
    if (stale) classes.push('is-stale');
    if (stateDescription === STATUS_CLOSED_REGULAR) classes.push('is-closed');
    else if (stateDescription === STATUS_OPEN_OUTSIDE) classes.push('is-open-outside');
    else classes.push('is-ok');
    return classes.join(' ');
};

const formatShopHours = (start, end) => {
    if (!start && !end) return 'Hours unavailable';
    return `${start || '?'} - ${end || '?'}`;
};

const isCoarsePointer = () =>
    typeof window !== 'undefined'
    && window.matchMedia('(max-width: 900px), (hover: none) and (pointer: coarse)').matches;

export const GateNowMap = () => {
    const { rows } = useGatesNow();
    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const popupRef = useRef(
        new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
    );
    const markersRef = useRef([]);
    const pinnedPopupRef = useRef(false);
    const popupHoverRef = useRef(false);
    const hidePopupTimerRef = useRef(null);
    const priorCameraRef = useRef(null);
    const [mapReady, setMapReady] = useState(false);
    const [mapSwitch, setMapSwitch] = useState(false);
    const [panelOpen, setPanelOpen] = useState(() => !isCoarsePointer());
    const [isMobileLayout, setIsMobileLayout] = useState(() => isCoarsePointer());
    const [selectedKey, setSelectedKey] = useState(null);

    useEffect(() => {
        const media = window.matchMedia('(max-width: 900px), (hover: none) and (pointer: coarse)');
        const sync = () => setIsMobileLayout(media.matches);
        sync();
        media.addEventListener('change', sync);
        return () => media.removeEventListener('change', sync);
    }, []);

    const gates = useMemo(() => {
        if (!Array.isArray(rows) || rows.length === 0) return {};

        return rows.reduce((acc, element) => {
            const startTime = element.StartTimeString
                ? format(new Date(`2000-01-01T${element.StartTimeString}`), 'hh:mm a')
                : '';
            const endTime = element.EndTimeString
                ? format(new Date(`2000-01-01T${element.EndTimeString}`), 'hh:mm a')
                : '';

            const gateRow = {
                StateDescription: element.StateDescription,
                StartTimeString: startTime,
                EndTimeString: endTime,
                IsDiscrepency: element.IsDiscrepency,
                Timestamp: element.Timestamp,
                Lat: element.Lat,
                Long: element.Long,
                PlaceName: element.PlaceName,
                SQLUnitName: element.SQLUnitName,
                IsStale: isSensorStale(element.Timestamp),
            };

            const key = element.SQLTenantName;
            const ageMinutes = getSensorAgeMinutes(element.Timestamp);

            if (acc[key]) {
                acc[key] = {
                    ...acc[key],
                    IsDiscrepency: acc[key].IsDiscrepency || element.IsDiscrepency,
                    StateDescription: [...acc[key].StateDescription, element.StateDescription],
                    Units: [...acc[key].Units, gateRow],
                    MaxLastSensorReadingMinutesAgo: Math.max(
                        acc[key].MaxLastSensorReadingMinutesAgo,
                        ageMinutes
                    ),
                };
            } else {
                acc[key] = {
                    SQLTenantName: key,
                    IsDiscrepency: element.IsDiscrepency,
                    StateDescription: [element.StateDescription],
                    StartTimeString: startTime,
                    EndTimeString: endTime,
                    MaxLastSensorReadingMinutesAgo: ageMinutes,
                    Units: [gateRow],
                };
            }
            return acc;
        }, {});
    }, [rows]);

    const unitsByKey = useMemo(() => (
        groupRowsByUnit(rows).map((unit) => {
            const StartTimeString = unit.RawStartTimeString
                ? format(new Date(`2000-01-01T${unit.RawStartTimeString}`), 'hh:mm a')
                : '';
            const EndTimeString = unit.RawEndTimeString
                ? format(new Date(`2000-01-01T${unit.RawEndTimeString}`), 'hh:mm a')
                : '';
            return {
                ...unit,
                StartTimeString,
                EndTimeString,
                gates: unit.gates.map((g) => ({ ...g, StartTimeString, EndTimeString })),
            };
        })
    ), [rows]);

    const discrepancyUnits = useMemo(() => (
        unitsByKey
            .filter((unit) => hasLiveDiscrepancy(unit.gates))
            .map((unit) => {
                const classification = classifyUnitDiscrepancy(unit.gates);
                if (!classification) return null;
                return {
                    ...unit,
                    ...classification,
                    Units: unit.gates,
                };
            })
            .filter(Boolean)
            .sort((a, b) => {
                if (a.severity !== b.severity) return a.severity - b.severity;
                const tenantCmp = a.SQLTenantName.localeCompare(b.SQLTenantName);
                if (tenantCmp !== 0) return tenantCmp;
                return a.SQLUnitName.localeCompare(b.SQLUnitName);
            })
    ), [unitsByKey]);

    const staleUnits = useMemo(() => (
        unitsByKey
            .map((unit) => {
                const staleGates = unit.gates.filter((g) => g.IsStale);
                if (staleGates.length === 0) return null;
                const oldest = Math.max(...staleGates.map((g) => getSensorAgeMinutes(g.Timestamp)));
                const { label } = formatRelativeSensorLabel(
                    staleGates.sort((a, b) => getSensorAgeMinutes(b.Timestamp) - getSensorAgeMinutes(a.Timestamp))[0].Timestamp
                );
                return {
                    ...unit,
                    staleCount: staleGates.length,
                    gateSummary: `${staleGates.length} of ${unit.gates.length} readings outdated`,
                    statusLabel: 'No recent sensor update',
                    color: getMarkerBackground(unit.gates.map((g) => g.StateDescription)),
                    ageLabel: label,
                    oldestMinutes: oldest,
                    Units: unit.gates,
                };
            })
            .filter(Boolean)
            .sort((a, b) => {
                if (b.oldestMinutes !== a.oldestMinutes) return b.oldestMinutes - a.oldestMinutes;
                const tenantCmp = a.SQLTenantName.localeCompare(b.SQLTenantName);
                if (tenantCmp !== 0) return tenantCmp;
                return a.SQLUnitName.localeCompare(b.SQLUnitName);
            })
    ), [unitsByKey]);

    const hidePopup = useCallback(() => {
        if (hidePopupTimerRef.current) {
            clearTimeout(hidePopupTimerRef.current);
            hidePopupTimerRef.current = null;
        }
        popupHoverRef.current = false;
        popupRef.current.remove();
    }, []);

    const scheduleHidePopup = useCallback(() => {
        if (pinnedPopupRef.current) return;
        if (hidePopupTimerRef.current) clearTimeout(hidePopupTimerRef.current);
        hidePopupTimerRef.current = setTimeout(() => {
            hidePopupTimerRef.current = null;
            if (pinnedPopupRef.current || popupHoverRef.current) return;
            popupRef.current.remove();
        }, 120);
    }, []);

    const showPopup = useCallback((location, tenant, map) => {
        if (hidePopupTimerRef.current) {
            clearTimeout(hidePopupTimerRef.current);
            hidePopupTimerRef.current = null;
        }

        const unitsHtml = tenant.Units.map((unit) => {
            const stale = unit.IsStale ?? isSensorStale(unit.Timestamp);
            return (
                `<div class="${unitPopupStatusClass(unit.StateDescription, stale)}">
                    <div class="cardPopup-unit-top">
                        <div class="cardPopup-unit-place">${unit.PlaceName}</div>
                        ${formatUnitAgeHtml(unit.Timestamp)}
                    </div>
                    <div class="cardPopup-unit-status">${unit.StateDescription}</div>
                </div>`
            );
        }).join('');

        const gateCount = tenant.Units.length;

        popupRef.current
            .setLngLat([location.lng, location.lat])
            .setHTML(`
                <div class="cardPopup">
                    <div class="cardPopup-header">
                        <div class="cardPopup-heading">
                            <h6 class="cardPopup-title">${tenant.SQLTenantName}</h6>
                            <div class="cardPopup-hours"><i class="fa-regular fa-clock" aria-hidden="true"></i>${formatShopHours(tenant.StartTimeString, tenant.EndTimeString)}</div>
                        </div>
                        <span class="cardPopup-count">${gateCount} ${gateCount === 1 ? 'gate' : 'gates'}</span>
                    </div>
                    <div class="cardPopup-units">${unitsHtml}</div>
                </div>
            `)
            .addTo(map);

        /* Keep the card open while scrolling its gate list (marker mouseleave alone would close it). */
        const popupEl = popupRef.current.getElement();
        if (popupEl) {
            const cardEl = popupEl.querySelector('.cardPopup');
            const mapRect = map.getContainer().getBoundingClientRect();
            const cardRect = cardEl?.getBoundingClientRect();
            if (cardEl && cardRect) {
                const margin = 12;
                const overflowBottom = cardRect.bottom - (mapRect.bottom - margin);
                const overflowTop = (mapRect.top + margin) - cardRect.top;
                const overflow = Math.max(overflowBottom, overflowTop, 0);
                if (overflow > 0) {
                    cardEl.style.maxHeight = `${Math.max(160, Math.floor(cardRect.height - overflow))}px`;
                }
            }

            popupEl.onmouseenter = () => {
                popupHoverRef.current = true;
                if (hidePopupTimerRef.current) {
                    clearTimeout(hidePopupTimerRef.current);
                    hidePopupTimerRef.current = null;
                }
            };
            popupEl.onmouseleave = () => {
                popupHoverRef.current = false;
                scheduleHidePopup();
            };
            const unitsEl = popupEl.querySelector('.cardPopup-units');
            if (unitsEl) {
                unitsEl.onwheel = (event) => event.stopPropagation();
            }
        }
    }, [scheduleHidePopup]);

    const clearMarkers = useCallback(() => {
        markersRef.current.forEach(({ marker, markerElement, handleMouseEnter, handleMouseLeave, handleClick }) => {
            markerElement.removeEventListener('mouseenter', handleMouseEnter);
            markerElement.removeEventListener('mouseleave', handleMouseLeave);
            markerElement.removeEventListener('click', handleClick);
            marker.remove();
        });
        markersRef.current = [];
    }, []);

    const collapseSelection = useCallback(() => {
        const map = mapRef.current;
        pinnedPopupRef.current = false;
        hidePopup();
        setSelectedKey(null);

        if (map && priorCameraRef.current) {
            map.flyTo({
                ...priorCameraRef.current,
                essential: true,
            });
            priorCameraRef.current = null;
        }
    }, [hidePopup]);

    const flyToUnit = useCallback((unitItem) => {
        const map = mapRef.current;
        if (!map || !unitItem?.Units?.length) return;

        if (selectedKey === unitItem.id) {
            collapseSelection();
            return;
        }

        const gate = unitItem.Units.find((u) => u.Long != null && u.Lat != null) || unitItem.Units[0];
        if (gate.Long == null || gate.Lat == null) return;

        if (!priorCameraRef.current) {
            priorCameraRef.current = {
                center: map.getCenter(),
                zoom: map.getZoom(),
            };
        }

        setSelectedKey(unitItem.id);
        pinnedPopupRef.current = true;
        map.flyTo({
            center: [gate.Long, gate.Lat],
            zoom: Math.max(map.getZoom(), isCoarsePointer() ? 16.5 : 17),
            essential: true,
        });
        showPopup(
            { lng: gate.Long, lat: gate.Lat },
            {
                SQLTenantName: `${unitItem.SQLTenantName} - ${unitItem.SQLUnitName}`,
                StartTimeString: unitItem.StartTimeString,
                EndTimeString: unitItem.EndTimeString,
                Units: unitItem.Units,
            },
            map
        );
    }, [showPopup, selectedKey, collapseSelection]);

    const styleUrl = mapSwitch
        ? 'mapbox://styles/mapbox/satellite-v9'
        : 'mapbox://styles/mapbox/light-v10';
    const appliedStyleRef = useRef(null);

    // Create the map once. Style toggles use setStyle so HTML markers stay put
    // instead of tearing down the whole map and reloading every sensor pin.
    useEffect(() => {
        const token = import.meta.env.VITE_MAPBOX_TOKEN;
        if (!token) {
            console.error('VITE_MAPBOX_TOKEN is missing');
            return undefined;
        }
        mapboxgl.accessToken = token;

        if (!mapContainerRef.current) return undefined;

        const initialStyle = mapSwitch
            ? 'mapbox://styles/mapbox/satellite-v9'
            : 'mapbox://styles/mapbox/light-v10';

        const mapInstance = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: initialStyle,
            center: AIR_SIDE_CENTER,
            zoom: isCoarsePointer() ? 15.6 : AIR_SIDE_ZOOM,
            attributionControl: false,
            cooperativeGestures: isCoarsePointer(),
        });

        mapRef.current = mapInstance;
        appliedStyleRef.current = initialStyle;

        const handleLoad = () => {
            setMapReady(true);
            // Warm the opposite basemap style so the first Sat toggle is snappier.
            const altStyle = initialStyle.includes('satellite')
                ? 'mapbox://styles/mapbox/light-v10'
                : 'mapbox://styles/mapbox/satellite-v9';
            const altUrl = altStyle.replace(
                'mapbox://styles/',
                `https://api.mapbox.com/styles/v1/`
            ) + `?access_token=${token}`;
            fetch(altUrl, { mode: 'cors', credentials: 'omit' }).catch(() => {});
        };
        const handleMapClick = () => {
            if (pinnedPopupRef.current) {
                collapseSelection();
            }
        };
        mapInstance.on('load', handleLoad);
        mapInstance.on('click', handleMapClick);

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => {
                mapInstance.resize();
            })
            : null;
        if (resizeObserver && mapContainerRef.current) {
            resizeObserver.observe(mapContainerRef.current);
        }

        return () => {
            mapInstance.off('load', handleLoad);
            mapInstance.off('click', handleMapClick);
            resizeObserver?.disconnect();
            clearMarkers();
            hidePopup();
            mapInstance.remove();
            mapRef.current = null;
            appliedStyleRef.current = null;
            setMapReady(false);
        };
        // Mount-only: style changes are handled below via setStyle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearMarkers, hidePopup, collapseSelection]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) return undefined;
        if (appliedStyleRef.current === styleUrl) return undefined;

        appliedStyleRef.current = styleUrl;
        // HTML Marker overlays survive setStyle; only basemap tiles swap.
        map.setStyle(styleUrl);

        const onStyleLoad = () => {
            map.resize();
        };
        map.once('style.load', onStyleLoad);
        return () => {
            map.off('style.load', onStyleLoad);
        };
    }, [mapSwitch, mapReady, styleUrl]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) return undefined;

        clearMarkers();

        const nextMarkers = Object.values(gates).map((tenant) => {
            const { Units, MaxLastSensorReadingMinutesAgo, StateDescription } = tenant;
            const location = Units.length > 0 && Units[0].Long != null && Units[0].Lat != null
                ? { lng: Units[0].Long, lat: Units[0].Lat }
                : null;

            if (!location) return null;

            const isStaleTenant = MaxLastSensorReadingMinutesAgo > STALE_MINUTES;
            const markerElement = document.createElement('div');
            markerElement.className = isStaleTenant ? 'acaa-marker-dashed' : 'acaa-marker';
            markerElement.style.background = getMarkerBackground(StateDescription);
            if (selectedKey && selectedKey.startsWith(`${tenant.SQLTenantName}|||`)) {
                markerElement.classList.add('acaa-marker-selected');
            }

            const marker = new mapboxgl.Marker(markerElement)
                .setLngLat([location.lng, location.lat])
                .addTo(map);

            const handleMouseEnter = () => {
                if (pinnedPopupRef.current) return;
                showPopup(location, tenant, map);
            };
            const handleMouseLeave = () => {
                scheduleHidePopup();
            };
            const handleClick = (event) => {
                event.stopPropagation();
                pinnedPopupRef.current = true;
                showPopup(location, tenant, map);
            };

            markerElement.addEventListener('mouseenter', handleMouseEnter);
            markerElement.addEventListener('mouseleave', handleMouseLeave);
            markerElement.addEventListener('click', handleClick);

            return { marker, markerElement, handleMouseEnter, handleMouseLeave, handleClick };
        }).filter(Boolean);

        markersRef.current = nextMarkers;

        return () => {
            clearMarkers();
        };
    }, [gates, mapReady, selectedKey, clearMarkers, scheduleHidePopup, showPopup]);

    const panelItemCount = discrepancyUnits.length + staleUnits.length;

    const renderPanelItem = (unitItem, kind) => {
        const isExpanded = selectedKey === unitItem.id;
        return (
            <li key={`${kind}-${unitItem.id}`}>
                <button
                    type="button"
                    className={`discrepancy-item ${kind === 'stale' ? 'is-stale' : ''} ${isExpanded ? 'is-selected is-expanded' : ''}`}
                    onClick={() => flyToUnit(unitItem)}
                    aria-expanded={isExpanded}
                >
                    <span
                        className="discrepancy-swatch"
                        style={{ background: unitItem.color }}
                    />
                    <span className="discrepancy-item-text">
                        <span className="discrepancy-tenant">
                            {unitItem.SQLTenantName} - {unitItem.SQLUnitName}
                        </span>
                        <span className="discrepancy-gates">
                            {unitItem.gateSummary}
                        </span>
                        <span
                            className={`discrepancy-status ${kind === 'stale' ? 'is-stale' : ''}`}
                            style={
                                kind !== 'stale' && unitItem.statusLabel === STATUS_CLOSED_REGULAR
                                    ? { color: COLORS.closed }
                                    : undefined
                            }
                        >
                            {unitItem.statusLabel}
                            {kind === 'stale' && unitItem.ageLabel ? ` · ${unitItem.ageLabel}` : ''}
                        </span>
                        <span className="discrepancy-meta">
                            Operating Hours: {formatShopHours(unitItem.StartTimeString, unitItem.EndTimeString)}
                        </span>
                        {isExpanded && (
                            <span className="discrepancy-sensor-list">
                                {unitItem.Units.map((gate, index) => {
                                    const { label, stale } = formatRelativeSensorLabel(gate.Timestamp);
                                    return (
                                        <span
                                            key={`${unitItem.id}-${gate.PlaceName || index}`}
                                            className={`discrepancy-sensor-row ${stale ? 'is-stale' : ''}`}
                                        >
                                            <span className="discrepancy-sensor-name">{gate.PlaceName || gate.SQLUnitName}</span>
                                            <span className="discrepancy-sensor-state">{gate.StateDescription}</span>
                                            <span className={`discrepancy-sensor-age ${stale ? 'is-stale' : ''}`}>{label}</span>
                                        </span>
                                    );
                                })}
                            </span>
                        )}
                    </span>
                </button>
            </li>
        );
    };

    return (
        <div id="GateNowMap">
            <div className="map-shell">
                <div className="map-controls">
                    <label className="satellite-toggle">
                        <span className="satellite-toggle-label">Satellite View</span>
                        <span className="satellite-toggle-short" aria-hidden="true">Sat</span>
                        <InputSwitch checked={mapSwitch} onChange={(e) => setMapSwitch(e.value)} />
                    </label>
                    <div className="map-title-chip">PIT Concessions</div>
                </div>

                <div className="map-stage">
                    <div ref={mapContainerRef} id="map2" className="map-canvas" />

                    <aside className={`discrepancy-panel ${panelOpen ? 'is-open' : 'is-collapsed'}`}>
                        <button
                            type="button"
                            className="discrepancy-panel-toggle"
                            onClick={() => setPanelOpen((open) => !open)}
                            aria-expanded={panelOpen}
                            aria-controls="discrepancy-panel-body"
                            aria-label={
                                panelOpen
                                    ? 'Hide partner variances'
                                    : `Show partner variances${panelItemCount ? ` (${panelItemCount})` : ''}`
                            }
                        >
                            <span className="discrepancy-panel-title">
                                Partner Variances
                                <span className={`discrepancy-count ${panelItemCount ? 'has-items' : ''}`}>
                                    {panelItemCount}
                                </span>
                            </span>
                            <i
                                className={`pi ${panelOpen ? 'pi-chevron-right' : 'pi-chevron-left'}`}
                                aria-hidden
                            />
                        </button>

                        {panelOpen && (
                            <div id="discrepancy-panel-body" className="discrepancy-panel-body">
                                {panelItemCount === 0 ? (
                                    <div className="discrepancy-empty">
                                        All tenants match schedule
                                    </div>
                                ) : (
                                    <>
                                        {discrepancyUnits.length > 0 && (
                                            <div className="panel-section">
                                                <ul className="discrepancy-list">
                                                    {discrepancyUnits.map((unitItem) => renderPanelItem(unitItem, 'discrepancy'))}
                                                </ul>
                                            </div>
                                        )}

                                        {staleUnits.length > 0 && (
                                            <div className="panel-section">
                                                <div className="panel-section-header">
                                                    Outdated readings
                                                    <span>{staleUnits.length}</span>
                                                </div>
                                                <ul className="discrepancy-list">
                                                    {staleUnits.map((unitItem) => renderPanelItem(unitItem, 'stale'))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </aside>

                    <div className="legend">
                        <div className="legend-row">
                            <div className="legend-item" style={{ backgroundColor: COLORS.closed }} />
                            <span className="legend-text">Closed during regular hours</span>
                        </div>
                        <div className="legend-row">
                            <div className="legend-item" style={{ backgroundColor: COLORS.partialClosed }} />
                            <span className="legend-text">Partially closed during regular hours</span>
                        </div>
                        <div className="legend-row">
                            <div className="legend-item" style={{ backgroundColor: COLORS.openOutside }} />
                            <span className="legend-text">Partially open during closed hours</span>
                        </div>
                        <div className="legend-row">
                            <div className="legend-item" style={{ backgroundColor: COLORS.ok }} />
                            <span className="legend-text">Open or closed as scheduled</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
